import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme, type ThemeMode } from "@/context/ThemeContext";
import { track } from "@/utils/track";
import { DOWNLOAD_PAGE_PATH } from "@/config/download";

const LANGUAGES = [
  { code: "zh-CN", label: "简体中文" },
  { code: "zh-TW", label: "繁體中文" },
  { code: "en", label: "English" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
];

const THEME_OPTIONS: { value: ThemeMode; label: string; icon: string }[] = [
  { value: "light", label: "浅色", icon: "ri-sun-line" },
  { value: "dark", label: "深色", icon: "ri-moon-line" },
  { value: "system", label: "跟随系统", icon: "ri-computer-line" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation("home");
  const { theme, effectiveTheme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(i18n.language || "zh-CN");
  const [bannerClosed, setBannerClosed] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const themeRef = useRef<HTMLDivElement>(null);
  const isLight = effectiveTheme === "light";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
      if (themeRef.current && !themeRef.current.contains(e.target as Node)) {
        setThemeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    setSelectedLang(i18n.language || "zh-CN");
  }, [i18n.language]);

  const scrollToSection = (id: string) => {
    if (isHome) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(`/#${id}`);
    }
  };

  const goHome = () => {
    if (!isHome) {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleLangChange = (code: string) => {
    i18n.changeLanguage(code);
    setSelectedLang(code);
    setLangOpen(false);
  };

  const handleThemeChange = (mode: ThemeMode) => {
    setTheme(mode);
    setThemeOpen(false);
  };

  const navItems = [
    { label: t("nav.home"), id: "hero" },
    { label: t("nav.scenarios"), id: "scenarios" },
    { label: t("nav.systems"), id: "systems" },
    { label: t("nav.engine"), id: "engine" },
    { label: t("nav.faq"), id: "faq" },
  ];

  // Theme-aware colors
  const navBgClass = scrolled || !isHome
    ? (isLight
        ? "bg-white/90 backdrop-blur-md border-b border-black/5"
        : "bg-cosmic-950/90 backdrop-blur-md border-b border-white/5")
    : "bg-transparent";

  const textColor = isLight ? "text-[#1c1c1e]" : "text-white";
  const textMuted = isLight ? "text-[#6e6e73]" : "text-silver-400";
  const textMutedHover = isLight ? "hover:text-[#1c1c1e]" : "hover:text-white";
  const borderColor = isLight ? "border-black/10" : "border-white/10";
  const borderHover = isLight ? "hover:border-black/25 hover:bg-black/5" : "hover:border-white/30 hover:bg-white/5";
  const iconColor = isLight ? "text-[#6e6e73]" : "text-white/60";

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      {/* Top Download Banner */}
      <AnimatePresence>
        {!bannerClosed && isHome && (
          <motion.div
            initial={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
            style={{
              backgroundColor: "var(--banner-bg)",
              borderBottom: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <div className="w-full mx-auto px-6 sm:px-10 lg:px-16 py-2.5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-xs sm:text-sm font-medium truncate" style={{ color: "var(--banner-text)" }}>
                    {t("banner.text")}
                  </span>
                  <div className="hidden sm:flex items-center gap-2 shrink-0">
                    <Link
                      to={DOWNLOAD_PAGE_PATH}
                      onClick={() => track("download_click", { toolId: "banner-download" })}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                        isLight
                          ? "bg-[#4a4a4c] text-white hover:bg-[#3a3a3c]"
                          : "bg-[#1F1030] text-white hover:bg-[#2D1B42]"
                      }`}
                    >
                      <i className="ri-download-2-line text-sm" />
                      {t("nav.download_now")}
                    </Link>
                  </div>
                </div>
                <button
                  onClick={() => setBannerClosed(true)}
                  className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full transition-colors duration-200 cursor-pointer"
                  style={{ color: "var(--banner-text)" }}
                  aria-label={t("banner.close")}
                >
                  <i className="ri-close-line text-sm" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Header */}
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className={`transition-all duration-500 ${navBgClass}`}
      >
        <div className="w-full mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex h-20 md:h-[5.5rem] items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer" onClick={goHome}>
              <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center shrink-0 ${isLight ? 'bg-primary-100/50' : ''}`}>
                <img
                  src="/images/app-icon-512.webp"
                  alt={t("about.brand")}
                  className="w-12 h-12 md:w-14 md:h-14 object-contain"
                />
              </div>
              <div className="flex flex-col">
                <span className={`text-[27px] md:text-[30px] font-bold tracking-tight ${textColor}`}>
                  {t("about.brand")}
                </span>
                <span className={`text-[11px] md:text-xs font-medium tracking-wider mt-0.5 ${isLight ? 'text-[#8a6a3e]' : 'text-gold-400/80'}`}>
                  {t("about.brand_slogan")}
                </span>
              </div>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm ${textMuted} ${textMutedHover} transition-colors duration-200 cursor-pointer whitespace-nowrap`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Right: Theme + Language + CTA */}
            <div className="flex items-center gap-3">
              {/* Theme Switcher */}
              <div className="relative" ref={themeRef}>
                <button
                  onClick={() => setThemeOpen(!themeOpen)}
                  className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full border transition-all duration-200 cursor-pointer ${borderColor} ${borderHover}`}
                  aria-label="切换主题"
                >
                  <i className={`${
                    effectiveTheme === "light" ? "ri-sun-line" : "ri-moon-line"
                  } ${iconColor} text-base md:text-lg`} />
                </button>

                <AnimatePresence>
                  {themeOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.96 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 top-full mt-2 w-44 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-[100] overflow-hidden"
                    >
                      {THEME_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => handleThemeChange(opt.value)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 cursor-pointer ${
                            theme === opt.value
                              ? "text-[#1c1c1e] font-semibold bg-[#f0f0f5]"
                              : "text-[#6e6e73] hover:text-[#1c1c1e] hover:bg-[#f0f0f5]"
                          }`}
                        >
                          <i className={`${opt.icon} text-base`} />
                          <span className="flex-1 text-left">{opt.label}</span>
                          {theme === opt.value && (
                            <i className="ri-check-line text-[#1c1c1e] text-sm" />
                          )}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Language Switcher */}
              <div className="relative" ref={langRef}>
                <button
                  onClick={() => setLangOpen(!langOpen)}
                  className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full border transition-all duration-200 cursor-pointer ${borderColor} ${borderHover}`}
                  aria-label={t("nav.switch_language")}
                >
                  <i className={`ri-global-line ${iconColor} text-base md:text-lg`} />
                </button>

                <AnimatePresence>
                  {langOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.96 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 top-full mt-2 w-44 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-[100] overflow-hidden"
                    >
                      {LANGUAGES.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => handleLangChange(lang.code)}
                          className={`w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 cursor-pointer ${
                            selectedLang === lang.code
                              ? "text-[#1c1c1e] font-semibold bg-[#f0f0f5]"
                              : "text-[#6e6e73] hover:text-[#1c1c1e] hover:bg-[#f0f0f5]"
                          }`}
                        >
                          {lang.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link
                to={DOWNLOAD_PAGE_PATH}
                onClick={() => track("download_click", { toolId: "navbar-download" })}
                className={`hidden sm:flex items-center gap-2 px-5 py-2 rounded-full border text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  isLight
                    ? "border-[#2d2d2d]/20 text-[#1c1c1e] hover:bg-[#f0f0f5]"
                    : "border-gold-500/40 text-gold-400 hover:bg-gold-500/10"
                }`}
              >
                {t("nav.download_now")}
              </Link>
            </div>
          </div>
        </div>
      </motion.header>
    </div>
  );
}